import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { NotificationService } from '@shared/service/notification.service';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import { AuthService } from '../service/auth.service';
import { decrypt, encrypt } from '../utils/encryption.util';
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private hotToast = inject(NotificationService);
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private apiPortResolved = false;
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // 1. One-time setup for Dynamic Ports
    this.applyApiPortOverrides();
    const isAuthEndpoint = req.url.includes('/auth/login') || req.url.includes('/auth/refresh');
    const needsPrimaryToken = this.isPrimaryApiUrl(req.url);
    // 2. Attach Tokens
    if (!isAuthEndpoint && needsPrimaryToken) {
      // Primary API: Attach User Access Token
      const token = this.authService.getAccessToken();
      if (token) {
        req = this.attachAuthHeader(req, token);
      }
    } else if (!needsPrimaryToken && this.shouldAttachStaticToken(req.url)) {
      // External API: Attach Static Token (Safeguarded)
      req = this.attachStaticToken(req);
    }
    // 3. Handle Request & Errors
    return next.handle(req).pipe(
      catchError(err => {
        if (!(err instanceof HttpErrorResponse)) {
          return throwError(() => err);
        }
        // Network error (status 0)
        if (err.status === 0) {
          this.hotToast.error('Network error - check connection');
          return throwError(() => err);
        }
        if (err.status === 406) {
          this.forceLogout();
          return EMPTY;
        }
        if (err.status === 401 && !isAuthEndpoint && needsPrimaryToken) {
          return this.handle401(req, next);
        }
        if (err.status === 429) {
          if (req.url.includes('/auth/refresh')) {
            this.forceLogout();
            return EMPTY;
          }
          this.hotToast.error('Server is busy (Too many requests). Please wait.');
          return throwError(() => err);
        }
        return throwError(() => err);
      })
    );
  }
  // ----------------------------------------------------
  // ------------------- HELPERS ------------------------
  // ----------------------------------------------------
  /**
   * Run once per app load. Reads query params from window.location
   * to override API endpoints (e.g. for local testing).
   */
  private applyApiPortOverrides() {
    if (this.apiPortResolved) return;
    this.apiPortResolved = true;
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const apiPort = searchParams.get('apiPort');
      const apiEndpoint = searchParams.get('apiEndpoint');
      if (apiPort) {
        environment.API_ENDPOINT = `http://localhost:${apiPort}/api/v3/`;
      }
      if (apiEndpoint) {
        const base = apiEndpoint.endsWith('/') ? apiEndpoint : `${apiEndpoint}/`;
        environment.API_ENDPOINT = base;
      }
    } catch (e) {
      // Safe to ignore in non-browser environments (SSR)
    }
  }
  private isPrimaryApiUrl(url: string): boolean {
    return (
      url.startsWith(environment.API_ENDPOINT) ||
      url.startsWith('https://consultant-api.globalrtis.com/api/v1/')
    );
  }
  /**
   * Determines if we should attach the static "Z47..." token.
   * Includes safeguards to prevent leaking this token to assets or unknown domains.
   */
  private shouldAttachStaticToken(url: string): boolean {
    // 0. BLOCK: Google APIs (e.g., YouTube). These use API keys or OAuth separately.
    if (url.includes('googleapis.com')) return false;
    // 1. BLOCK: Common static assets (Images, Scripts, Fonts)
    // Attaching auth headers to these often causes CORS errors.
    const isAsset = /\.(png|jpg|jpeg|svg|gif|css|js|woff|woff2|json)$/i.test(url);
    if (isAsset) return false;
    // 2. OPTIONAL: Whitelist specific domains (Best Practice)
    // Uncomment the lines below to strictly limit where this token goes.
    /*
    const allowedDomains = [
      'https://api.some-partner.com',
      'https://another-secure-service.com'
    ];
    if (!allowedDomains.some(domain => url.startsWith(domain))) {
       return false;
    }
    */
    return true;
  }
  private attachStaticToken(req: HttpRequest<any>) {
    return req.clone({
      setHeaders: {
        Authorization: 'Bearer Z47SIO6VZFAWXK2FK3GNYT3YIUFKXIOARZCFNQWLEHUOIY57'
      }
    });
  }
  private handle401(req: HttpRequest<any>, next: HttpHandler) {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(t => !!t),
        take(1),
        switchMap(token => next.handle(this.attachAuthHeader(req, token!)))
      );
    }
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);
    return this.refreshToken().pipe(
      switchMap(token => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(token);
        return next.handle(this.attachAuthHeader(req, token));
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.forceLogout();
        return throwError(() => err);
      })
    );
  }
  private refreshToken(): Observable<string> {
    const refresh = this.getRefreshToken();
    if (!refresh) {
      return throwError(() => 'No refresh token available');
    }
    return this.authService.refreshToken({ refreshToken: refresh }).pipe(
      map(res => {
        const access = res?.data?.accessToken;
        if (!access) throw new Error('Refresh response missing access token');
        localStorage.setItem('token', encrypt(access));
        return access;
      })
    );
  }
  private attachAuthHeader(req: HttpRequest<any>, token: string) {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }
  private getRefreshToken(): string | null {
    const t = localStorage.getItem('refresh-token');
    return t ? decrypt(t) : null;
  }
  private forceLogout() {
    try {
      localStorage.clear();
    } catch {
      // Storage can be unavailable in restricted browser contexts.
    }
    this.authService.logout();
  }
}
